import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getStaff,
  createStaff,
  inviteStaff,
  updateStaff,
  deactivateStaff,
  activateStaff,
  getPermissionsInfo,
  getDirectorCount,
  getStaffCountries,
  setStaffCountries,
  bootstrapDirector,
} from '../api/admin';
import { useAuth } from '../context/AuthContext';
import Badge from '../components/Badge';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import styles from './Staff.module.css';

const ROLE_OPTIONS = [
  { value: 'company_director', label: 'Company Director', desc: 'Co-founder tier: full access, manages directors and all staff' },
  { value: 'super_admin', label: 'Super Admin', desc: 'Full staff access (except directors), all app/verif/user permissions' },
  { value: 'country_manager', label: 'Country Manager', desc: 'Manages users, apps, verifications — no staff' },
  { value: 'support_agent', label: 'Support Agent', desc: 'Handles applications and verifications' },
  // Development Plan, Phase 3 — the restricted profile: sees only My Tasks
  // and Case Review, nothing else, enforced on the server regardless of
  // what's ticked below. Self-claim stays off unless explicitly granted.
  { value: 'contractor', label: 'Contractor', desc: 'Restricted: only My Tasks and Case Review — no other admin area, even via direct URL' },
];

const ROLE_LABELS = {
  company_director: 'Company Director',
  super_admin: 'Super Admin',
  country_manager: 'Country Manager',
  support_agent: 'Support Agent',
  contractor: 'Contractor',
};

const PERMISSION_DEFS = [
  { key: 'manage_applications', label: 'Manage Applications', desc: 'Review, approve, reject vehicle owner apps' },
  { key: 'manage_verifications', label: 'Manage Verifications', desc: 'Approve/reject identity verifications' },
  { key: 'manage_users', label: 'Manage Users', desc: 'View, block, unblock, delete customer accounts' },
  { key: 'view_logs', label: 'View Activity Log', desc: 'Access the admin activity audit log' },
  { key: 'manage_staff', label: 'Manage Staff', desc: 'Invite, edit, deactivate non-director admin accounts' },
  { key: 'manage_protocols', label: 'Manage Protocols', desc: 'View vehicle handover and return reports' },
  { key: 'view_protocol_locations', label: 'View Protocol Locations', desc: 'View protected GPS handover and return locations' },
  { key: 'manage_directors', label: 'Manage Directors', desc: 'Appoint/modify other Company Directors (co-founders only)' },
  { key: 'manage_safety', label: 'Manage Safety Center', desc: 'Triage incident reports, manage emergency numbers' },
  { key: 'manage_country_fees', label: 'Manage country fees', desc: 'Standard NULA-MOVE booking fee per country (amounts and percentages)' },
  { key: 'manage_individual_fees', label: 'Manage individual fees & exemptions', desc: 'Individual booking fees, commissions and fee exemptions per account' },
  { key: 'correct_provider_taxes', label: 'Correct provider taxes', desc: "Correct a provider's taxes and local levies on their listings" },
  { key: 'self_claim_cases', label: 'Self-claim cases', desc: 'May pick open cases in Case Control and take them with a personal PIN' },
  // Permissions & History Development Plan (13.10.2026) — the 3 remaining
  // action-right switches a restricted account starts without. Releasing
  // any of these grants no director, finance or general admin rights.
  { key: 'case_view_open_pool', label: 'View open assignments', desc: 'See the unassigned open pool in Case Control (does not allow claiming it — see Self-claim cases)' },
  { key: 'case_view_others', label: 'View others’ assignments', desc: 'See other people’s current assignments and completed history in Case Control, within their own country/case-type access' },
  { key: 'case_assign_to_others', label: 'Assign to other people', desc: 'May assign a case to another staff member (not the full Owner Case Control — no escalate, take-over, settings or emergency access)' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function PermChips({ permissions }) {
  if (!permissions) return null;
  return (
    <div className={styles.permChips}>
      {PERMISSION_DEFS.map((p) => (
        <span
          key={p.key}
          className={`${styles.permChip} ${permissions[p.key] ? styles.on : styles.off}`}
          title={p.desc}
        >
          {permissions[p.key] ? '✓' : '✗'} {p.label.split(' ')[1] || p.label}
        </span>
      ))}
    </div>
  );
}

// Permissions & History Development Plan (13.10.2026) — "internal" or
// "external" is only a display/account-profile label used as the default
// bucket for the History & Document Rights settings page; it never grants
// rights on its own (enforced server-side, not just by this form).
const ACCOUNT_PROFILE_OPTIONS = [
  { value: 'external', label: 'External', desc: 'Outside contractor / partner staff' },
  { value: 'internal', label: 'Internal', desc: 'Nula Move employee' },
];

const emptyForm = {
  email: '',
  password: '',
  full_name: '',
  phone: '',
  role_type: 'support_agent',
  account_profile: 'external',
  country_code: '',
  permissions: {
    manage_applications: true,
    manage_verifications: true,
    manage_users: false,
    view_logs: false,
    manage_staff: false,
    manage_directors: false,
    manage_protocols: true,
    view_protocol_locations: false,
    manage_safety: true,
  },
};

function getRoleDefaults(roleType) {
  switch (roleType) {
    case 'company_director':
      return {
        manage_applications: true,
        manage_verifications: true,
        manage_users: true,
        view_logs: true,
        manage_staff: true,
        manage_directors: true,
        manage_protocols: true,
        view_protocol_locations: true,
        manage_safety: true,
      };
    case 'super_admin':
      return {
        manage_applications: true,
        manage_verifications: true,
        manage_users: true,
        view_logs: true,
        manage_staff: true,
        manage_directors: false,
        manage_protocols: true,
        view_protocol_locations: true,
        manage_safety: true,
      };
    case 'country_manager':
      return {
        manage_applications: true,
        manage_verifications: true,
        manage_users: true,
        view_logs: true,
        manage_staff: false,
        manage_directors: false,
        manage_protocols: true,
        view_protocol_locations: true,
        manage_safety: true,
      };
    case 'contractor':
      return {
        manage_applications: false,
        manage_verifications: false,
        manage_users: false,
        view_logs: false,
        manage_staff: false,
        manage_directors: false,
        manage_protocols: false,
        view_protocol_locations: false,
        manage_safety: false,
        self_claim_cases: false,
      };
    case 'support_agent':
    default:
      return {
        manage_applications: true,
        manage_verifications: true,
        manage_users: false,
        view_logs: false,
        manage_staff: false,
        manage_directors: false,
        manage_protocols: true,
        view_protocol_locations: false,
        manage_safety: true,
      };
  }
}

export default function Staff() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addMode, setAddMode] = useState('invite'); // 'invite' | 'direct'
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [inviteResult, setInviteResult] = useState(null); // { accept_url, expires_at, email }
  const [grantedCountries, setGrantedCountries] = useState([]); // multi-country access for the admin being edited
  const [savingCountries, setSavingCountries] = useState(false);

  const params = {
    page,
    limit: 20,
    ...(search && { search }),
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['staff', params],
    queryFn: () => getStaff(params).then((r) => r.data),
    keepPreviousData: true,
  });

  const { data: permInfo } = useQuery({
    queryKey: ['permissions-info'],
    queryFn: () => getPermissionsInfo().then((r) => r.data),
    staleTime: 60_000,
    retry: false,
  });

  const canManageStaff = permInfo?.permissions?.manage_staff ?? true;
  const canManageDirectors = permInfo?.permissions?.manage_directors ?? false;
  const userRoleType = permInfo?.adminRole?.role_type || (currentUser?.user_role === 'admin' ? 'super_admin' : null);
  const isCompanyDirector = canManageDirectors === true && userRoleType === 'company_director';

  const { data: directorCountData } = useQuery({
    queryKey: ['director-count'],
    queryFn: () => getDirectorCount().then((r) => r.data),
    enabled: canManageStaff,
    staleTime: 30_000,
    retry: false,
  });

  const bootstrapMutation = useMutation({
    mutationFn: () => bootstrapDirector().then((r) => r.data),
    onSuccess: () => {
      toast.success('Done — you are now a Company Director. Sign out and back in to refresh all permissions.');
      queryClient.invalidateQueries({ queryKey: ['director-count'] });
      queryClient.invalidateQueries({ queryKey: ['permissions-info'] });
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      setTimeout(() => window.location.reload(), 1200);
    },
    onError: (err) => {
      const data = err.response?.data || {};
      const msg = data.error || err.message || 'Failed to bootstrap director tier';
      const hint = data.hint ? `${data.hint}\n\n${data.manual_sql || ''}` : '';
      if (hint) {
        toast.error(
          (t) => (
            <div style={{ maxWidth: 520, lineHeight: 1.45 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{msg}</div>
              <div style={{ fontSize: 12, color: '#475569', whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, monospace' }}>
                {hint}
              </div>
            </div>
          ),
          { duration: 15000 }
        );
      } else {
        toast.error(msg);
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: (d) => createStaff(d),
    onSuccess: () => {
      toast.success('Staff account created');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      setAddModalOpen(false);
      setForm({ ...emptyForm });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to create staff'),
  });

  const inviteMutation = useMutation({
    mutationFn: (d) => {
      const payload = { ...d };
      delete payload.password;
      return inviteStaff(payload).then((r) => r.data);
    },
    onSuccess: (result) => {
      toast.success('Invitation created — copy the link to send it.');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      setInviteResult({
        accept_url: result.accept_url,
        expires_at: result.expires_at,
        email: result.invite?.email || form.email,
      });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to create invitation'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateStaff(id, data),
    onSuccess: () => {
      toast.success('Staff account updated');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      setEditModalOpen(false);
      setEditingId(null);
      setForm({ ...emptyForm });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to update staff'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id) => deactivateStaff(id),
    onSuccess: () => {
      toast.success('Staff deactivated');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to deactivate'),
  });

  const activateMutation = useMutation({
    mutationFn: (id) => activateStaff(id),
    onSuccess: () => {
      toast.success('Staff activated');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to activate'),
  });

  function handleSearch(e) {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  }

  function handleClearSearch() {
    setSearchInput('');
    setSearch('');
    setPage(1);
  }

  function handleFilter(setter, val) {
    setter(val);
    setPage(1);
  }

  function openAddModal() {
    setForm({ ...emptyForm });
    setAddModalOpen(true);
  }

  function openEditModal(staffMember) {
    setEditingId(staffMember.id);
    setForm({
      email: staffMember.email,
      password: '',
      full_name: staffMember.full_name || '',
      phone: staffMember.phone || '',
      role_type: staffMember.role_type || 'support_agent',
      account_profile: staffMember.account_profile || 'external',
      country_code: staffMember.country_code || '',
      permissions: { ...staffMember.permissions } || { ...emptyForm.permissions },
      is_active: staffMember.is_active,
    });
    getStaffCountries(staffMember.id).then((res) => setGrantedCountries(res.data?.countries || [])).catch(() => setGrantedCountries([]));
    setEditModalOpen(true);
  }

  function toggleGrantedCountry(code) {
    setGrantedCountries((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  }

  async function saveGrantedCountries() {
    setSavingCountries(true);
    try {
      await setStaffCountries(editingId, grantedCountries);
      toast.success('Country access updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update country access');
    } finally {
      setSavingCountries(false);
    }
  }

  function handleRoleChange(roleType) {
    const safeRoleType =
      roleType === 'company_director' && !isCompanyDirector
        ? (form.role_type || 'support_agent')
        : roleType;
    if (roleType === 'company_director' && !isCompanyDirector) {
      toast.error('Only Company Directors can assign the Company Director role.');
    }
    const defaults = getRoleDefaults(safeRoleType);
    setForm((prev) => ({
      ...prev,
      role_type: safeRoleType,
      permissions: { ...defaults, ...prev.permissions },
    }));
  }

  function togglePerm(key) {
    if (key === 'manage_directors' && !isCompanyDirector) {
      toast.error('Only Company Directors can toggle the Manage Directors permission.');
      return;
    }
    setForm((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
    }));
  }

  function handleCreateSubmit(e) {
    e.preventDefault();
    if (!form.email || !form.full_name) {
      toast.error('Email and full name are required');
      return;
    }
    if (form.role_type === 'company_director' && !isCompanyDirector) {
      toast.error('Only Company Directors can assign the Company Director role. Your selection was reset.');
      const safeDefaults = getRoleDefaults('super_admin');
      setForm((prev) => ({ ...prev, role_type: 'super_admin', permissions: safeDefaults }));
      return;
    }
    if (form.permissions?.manage_directors === true && !isCompanyDirector) {
      toast.error('Only Company Directors can grant the Manage Directors permission.');
      setForm((prev) => ({
        ...prev,
        permissions: { ...prev.permissions, manage_directors: false },
      }));
      return;
    }
    if (addMode === 'direct') {
      if (!form.password) {
        toast.error('Password is required for direct creation');
        return;
      }
      if (form.password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      createMutation.mutate({
        email: form.email,
        password: form.password,
        full_name: form.full_name,
        phone: form.phone || null,
        role_type: form.role_type,
        account_profile: form.account_profile,
        country_code: form.country_code || '',
        permissions: form.permissions,
      });
    } else {
      inviteMutation.mutate({
        email: form.email,
        full_name: form.full_name,
        phone: form.phone || null,
        role_type: form.role_type,
        account_profile: form.account_profile,
        country_code: form.country_code || '',
        permissions: form.permissions,
      });
    }
  }

  function handleUpdateSubmit(e) {
    e.preventDefault();
    if (!form.full_name) {
      toast.error('Full name is required');
      return;
    }
    if (form.role_type === 'company_director' && !isCompanyDirector) {
      toast.error('Only Company Directors can change the Company Director role.');
      return;
    }
    if (form.permissions?.manage_directors === true && !isCompanyDirector) {
      toast.error('Only Company Directors can grant the Manage Directors permission.');
      setForm((prev) => ({
        ...prev,
        permissions: { ...prev.permissions, manage_directors: false },
      }));
      return;
    }
    const payload = {
      full_name: form.full_name,
      phone: form.phone || null,
      role_type: form.role_type,
      account_profile: form.account_profile,
      country_code: form.country_code || '',
      permissions: form.permissions,
      is_active: form.is_active,
    };
    if (form.password && form.password.length > 0) {
      if (form.password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      payload.password = form.password;
    }
    updateMutation.mutate({ id: editingId, data: payload });
  }

  useEffect(() => {
    const handler = () => setOpenDropdownId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  let staffList = data?.staff || [];
  if (statusFilter === 'active') staffList = staffList.filter((s) => s.is_active);
  if (statusFilter === 'inactive') staffList = staffList.filter((s) => !s.is_active);

  const pagination = data?.pagination;

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>Staff Management</h1>
          {pagination && <span className={styles.total}>{pagination.total} total</span>}
        </div>
        {canManageStaff && (
          <button
            className={styles.addBtn}
            onClick={openAddModal}
            disabled={createMutation.isPending}
          >
            <span>＋</span> Add Staff Member
          </button>
        )}
      </div>

      {directorCountData && (directorCountData.count ?? 0) === 0 && permInfo && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderRadius: 10,
            background: 'linear-gradient(135deg,#fff7ed 0%,#fef3c7 100%)',
            border: '1px solid #fcd34d',
            marginBottom: 14,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: '#92400e', fontSize: 15, marginBottom: 4 }}>
              👑 No Company Director exists yet — bootstrap the first co-founder account
            </div>
            <div style={{ color: '#78350f', fontSize: 13, lineHeight: 1.5 }}>
              There are <b>0 directors</b> in the system. To unlock the Company Director tier (and the
              ability to appoint/invite additional co-founder directors), promote your currently signed-in
              admin account once. This is a <b>one-shot</b> action — afterwards it disappears and only
              existing directors can create more directors.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => {
                if (!window.confirm('Promote YOUR current admin account into the first Company Director (co-founder) tier?\n\nThis action is one-shot and cannot be undone by non-directors afterwards.')) return;
                bootstrapMutation.mutate();
              }}
              disabled={bootstrapMutation.isPending}
              style={{
                padding: '11px 18px',
                background: '#b45309',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: bootstrapMutation.isPending ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 700,
                opacity: bootstrapMutation.isPending ? 0.7 : 1,
              }}
            >
              {bootstrapMutation.isPending ? 'Promoting…' : '⭐ Promote Me to Company Director (One-Time)'}
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          borderRadius: 8,
          background: permInfo?.adminRole?.role_type === 'company_director' ? '#ecfeff' : '#f8fafc',
          border: '1px solid',
          borderColor: permInfo?.adminRole?.role_type === 'company_director' ? '#a5f3fc' : '#e2e8f0',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <span style={{ fontWeight: 600, color: '#0f172a' }}>
            Your access:
            <span
              style={{
                marginLeft: 8,
                padding: '3px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                background: permInfo?.adminRole?.role_type === 'company_director' ? '#0d9488' : permInfo?.adminRole?.role_type === 'super_admin' ? '#2563eb' : permInfo?.adminRole?.role_type === 'country_manager' ? '#d97706' : '#475569',
                color: '#fff',
              }}
            >
              {permInfo?.adminRole?.role_type ? ROLE_LABELS[permInfo.adminRole.role_type] : 'Super Admin (bootstrap default)'}
            </span>
          </span>
          {permInfo?.adminRole?.role_type !== 'company_director' && (
            <span style={{ fontSize: 13, color: '#475569' }}>
              💡 The <b>Company Director</b> role option and the Manage Directors permission are only visible/editable when signed in as a fellow Company Director (co-founder tier).
            </span>
          )}
          {permInfo?.adminRole?.role_type === 'company_director' && (
            <span style={{ fontSize: 13, color: '#0f766e' }}>
              🪪 Co-founder mode: you can appoint/modify other Company Directors and every other staff tier.
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PERMISSION_DEFS.map((p) => (
            <span
              key={p.key}
              title={p.desc}
              style={{
                padding: '3px 8px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                background: permInfo?.permissions?.[p.key] ? '#dcfce7' : '#f1f5f9',
                color: permInfo?.permissions?.[p.key] ? '#166534' : '#64748b',
                border: '1px solid',
                borderColor: permInfo?.permissions?.[p.key] ? '#86efac' : '#e2e8f0',
              }}
            >
              {permInfo?.permissions?.[p.key] ? '✓' : '✗'} {p.label.split(' ')[1] || p.label}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.filterBar}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by name or email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className={styles.searchBtn}>Search</button>
          {search && (
            <button type="button" className={styles.clearBtn} onClick={handleClearSearch}>
              Clear
            </button>
          )}
        </form>
        <select
          className={styles.select}
          value={statusFilter}
          onChange={(e) => handleFilter(setStatusFilter, e.target.value)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {isLoading && <p className={styles.msg}>Loading…</p>}
      {isError && <p className={styles.msgError}>Failed to load staff.</p>}

      {data && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Permissions</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staffList.length === 0 && (
                  <tr>
                    <td colSpan={8} className={styles.empty}>
                      {search || statusFilter ? 'No matching staff members.' : 'No staff members yet. Click "Add Staff Member" to invite one.'}
                    </td>
                  </tr>
                )}
                {staffList.map((s) => {
                  const isSelf = s.id === currentUser?.id;
                  return (
                    <tr key={s.id}>
                      <td className={styles.name}>{s.full_name || '—'}</td>
                      <td>
                        <div>{s.email}</div>
                        {s.phone && <div className={styles.email}>{s.phone}</div>}
                      </td>
                      <td>
                        <Badge
                          value={s.role_type || 'unassigned'}
                          label={ROLE_LABELS[s.role_type] || 'Unassigned'}
                        />
                        <span style={{ display: 'block', marginTop: 3, fontSize: 10.5, color: '#9ca3af' }} title="Account profile — a label only, used by History & Document Rights settings">
                          {s.account_profile === 'internal' ? 'Internal' : 'External'}
                        </span>
                      </td>
                      <td>
                        <PermChips permissions={s.permissions} />
                      </td>
                      <td>
                        <Badge
                          value={s.is_active ? 'active' : 'inactive'}
                          label={s.is_active ? 'Active' : 'Inactive'}
                        />
                      </td>
                      <td>{fmt(s.created_at)}</td>
                      <td>{fmt(s.last_login)}</td>
                      <td>
                        <div style={{ position: 'relative' }}>
                          <button
                            style={{ padding: '5px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(openDropdownId === s.id ? null : s.id);
                            }}
                          >
                            ⋯ Actions
                          </button>
                          {openDropdownId === s.id && (
                            <div
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: '100%',
                                marginTop: '4px',
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                zIndex: 10,
                                minWidth: '170px',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {canManageStaff && (
                                <button
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '10px 16px',
                                    textAlign: 'left',
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    color: '#374151',
                                  }}
                                  onClick={() => {
                                    setOpenDropdownId(null);
                                    openEditModal(s);
                                  }}
                                >
                                  ✏️ Edit Staff
                                </button>
                              )}
                              {canManageStaff && !isSelf && (
                                s.is_active ? (
                                  <button
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      padding: '10px 16px',
                                      textAlign: 'left',
                                      border: 'none',
                                      background: 'none',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      color: '#dc2626',
                                    }}
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      if (window.confirm(`Deactivate ${s.full_name || s.email}? They will not be able to log in.`)) {
                                        deactivateMutation.mutate(s.id);
                                      }
                                    }}
                                    disabled={deactivateMutation.isPending}
                                  >
                                    🚫 Deactivate
                                  </button>
                                ) : (
                                  <button
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      padding: '10px 16px',
                                      textAlign: 'left',
                                      border: 'none',
                                      background: 'none',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      color: '#16a34a',
                                    }}
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      if (window.confirm(`Reactivate ${s.full_name || s.email}?`)) {
                                        activateMutation.mutate(s.id);
                                      }
                                    }}
                                    disabled={activateMutation.isPending}
                                  >
                                    ✅ Activate
                                  </button>
                                )
                              )}
                              {isSelf && (
                                <div
                                  style={{
                                    padding: '8px 16px',
                                    fontSize: '12px',
                                    color: '#9ca3af',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  (This is your account)
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            page={pagination?.page || 1}
            pages={pagination?.pages || 1}
            onPage={setPage}
          />
        </>
      )}

      {/* Add Staff Modal */}
      {addModalOpen && (
        <Modal
          title="Add New Staff Member"
          onClose={() => { setAddModalOpen(false); setForm({ ...emptyForm }); setInviteResult(null); }}
        >
          {inviteResult ? (
            <div>
              <div style={{
                padding: '14px',
                background: '#ecfdf5',
                border: '1px solid #a7f3d0',
                borderRadius: '10px',
                marginBottom: '16px',
              }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#065f46', marginBottom: '6px' }}>
                  🎉 Invitation created for {inviteResult.email}
                </div>
                <div style={{ fontSize: '12px', color: '#047857' }}>
                  Expires {new Date(inviteResult.expires_at).toLocaleString()}
                </div>
              </div>
              <div className={styles.formRow}>
                <label>Accept URL (send this to the invitee)</label>
                <input
                  readOnly
                  value={inviteResult.accept_url}
                  onClick={(e) => e.currentTarget.select()}
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
                <small>Click the box above to select, then Ctrl+C to copy.</small>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                <button
                  type="button"
                  className={styles.saveBtn}
                  onClick={() => {
                    navigator.clipboard?.writeText(inviteResult.accept_url);
                    toast.success('Accept link copied to clipboard');
                  }}
                >
                  📋 Copy Link
                </button>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => {
                    setInviteResult(null);
                    setForm({ ...emptyForm });
                  }}
                >
                  Invite Another
                </button>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => { setAddModalOpen(false); setForm({ ...emptyForm }); setInviteResult(null); }}
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleCreateSubmit}>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
                <button
                  type="button"
                  onClick={() => setAddMode('invite')}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    border: 'none',
                    background: addMode === 'invite' ? '#fff' : 'transparent',
                    color: addMode === 'invite' ? '#111827' : '#64748b',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: addMode === 'invite' ? 600 : 500,
                    cursor: 'pointer',
                    boxShadow: addMode === 'invite' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  }}
                >
                  ✉️ Invite via Email (Recommended)
                </button>
                <button
                  type="button"
                  onClick={() => setAddMode('direct')}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    border: 'none',
                    background: addMode === 'direct' ? '#fff' : 'transparent',
                    color: addMode === 'direct' ? '#111827' : '#64748b',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: addMode === 'direct' ? 600 : 500,
                    cursor: 'pointer',
                    boxShadow: addMode === 'direct' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  }}
                >
                  🔑 Create with Password
                </button>
              </div>

              <div className={styles.formRow}>
                <label>Full Name *</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Jane Admin"
                />
              </div>
              <div className={styles.formRow}>
                <label>Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@nulamove.com"
                />
              </div>
              <div className={styles.formRow}>
                <label>Phone</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 555-0100"
                />
              </div>
              {addMode === 'direct' && (
                <div className={styles.formRow}>
                  <label>Temporary Password *</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Min 8 characters"
                  />
                  <small>Staff member can change this after login.</small>
                </div>
              )}
              <div className={styles.formRow}>
                <label>Role</label>
                <select
                  value={form.role_type}
                  onChange={(e) => handleRoleChange(e.target.value)}
                >
                  {ROLE_OPTIONS.map((r) => {
                    if (r.value === 'company_director' && !isCompanyDirector) return null;
                    return (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    );
                  })}
                </select>
                <small>
                  {ROLE_OPTIONS.find((r) => r.value === form.role_type)?.desc}
                </small>
                {!isCompanyDirector && (
                  <small style={{ color: '#475569', marginTop: 4, display: 'inline-block' }}>
                    💡 The Company Director (co-founder) role is only visible/assignable when signed in as an existing Company Director.
                  </small>
                )}
              </div>
              <div className={styles.formRow}>
                <label>Account profile</label>
                <select
                  value={form.account_profile}
                  onChange={(e) => setForm({ ...form, account_profile: e.target.value })}
                >
                  {ACCOUNT_PROFILE_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <small>{ACCOUNT_PROFILE_OPTIONS.find((p) => p.value === form.account_profile)?.desc} — a label only, used as the default for History &amp; Document Rights settings. Never grants rights by itself.</small>
              </div>
              <div className={styles.formRow}>
                <small>Country access (USA / Kenya) is granted after this account is created — open it from the list and use "Country access" there.</small>
              </div>

              <div className={styles.permSection}>
                <div className={styles.permSectionTitle}>Individual Access Permissions</div>
                <div className={styles.permGrid}>
                  {PERMISSION_DEFS.map((p) => (
                    <label key={p.key} className={styles.permItem}>
                      <input
                        type="checkbox"
                        checked={!!form.permissions[p.key]}
                        onChange={() => togglePerm(p.key)}
                      />
                      <div className={styles.permLabel}>
                        <strong>{p.label}</strong>
                        <span>{p.desc}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => { setAddModalOpen(false); setForm({ ...emptyForm }); setInviteResult(null); }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.saveBtn}
                  disabled={createMutation.isPending || inviteMutation.isPending}
                >
                  {inviteMutation.isPending
                    ? 'Sending Invite…'
                    : createMutation.isPending
                    ? 'Creating…'
                    : addMode === 'invite'
                    ? 'Generate Invite Link'
                    : 'Create Staff Account'}
                </button>
              </div>
            </form>
          )}
        </Modal>
      )}

      {/* Edit Staff Modal */}
      {editModalOpen && (
        <Modal
          title="Edit Staff Member"
          onClose={() => { setEditModalOpen(false); setEditingId(null); setForm({ ...emptyForm }); }}
        >
          <form onSubmit={handleUpdateSubmit}>
            <div className={styles.roleInfo}>
              Editing <strong>{form.full_name || form.email}</strong>. Changes take effect on their next request.
            </div>
            <div className={styles.formRow}>
              <label>Full Name *</label>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              />
              <small>Admin names are editable any time from this screen.</small>
            </div>
            <div className={styles.formRow}>
              <label>Email (read-only)</label>
              <input
                type="email"
                value={form.email}
                disabled
                style={{ background: '#f1f5f9', color: '#64748b' }}
              />
            </div>
            <div className={styles.formRow}>
              <label>Phone</label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className={styles.formRow}>
              <label>Reset Password (leave blank to keep current)</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 8 characters"
              />
            </div>
            <div className={styles.formRow}>
              <label>Role</label>
              <select
                value={form.role_type}
                onChange={(e) => handleRoleChange(e.target.value)}
                disabled={form.role_type === 'company_director' && !isCompanyDirector}
              >
                {ROLE_OPTIONS.map((r) => {
                  if (r.value === 'company_director' && !isCompanyDirector) return null;
                  return (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  );
                })}
              </select>
              <small>
                {ROLE_OPTIONS.find((r) => r.value === form.role_type)?.desc}
              </small>
              {form.role_type === 'company_director' && !isCompanyDirector && (
                <small style={{ color: '#b91c1c' }}>Only fellow Company Directors can change the director role.</small>
              )}
            </div>
            <div className={styles.formRow}>
              <label>Country access</label>
              {['company_director', 'super_admin'].includes(form.role_type) ? (
                <small>This role is unrestricted — always sees every country, including "All countries".</small>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 14, marginTop: 4 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <input type="checkbox" checked={grantedCountries.includes('US')} onChange={() => toggleGrantedCountry('US')} /> USA
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <input type="checkbox" checked={grantedCountries.includes('KE')} onChange={() => toggleGrantedCountry('KE')} /> Kenya
                    </label>
                  </div>
                  <small>An admin can be granted more than one country. They only see "All countries" among their own granted countries if they have more than one.</small>
                  <button type="button" onClick={saveGrantedCountries} disabled={savingCountries}
                    style={{ marginTop: 6, padding: '6px 14px', borderRadius: 6, border: 'none', background: '#0B6A4A', color: 'white', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
                    {savingCountries ? 'Saving…' : 'Save country access'}
                  </button>
                </>
              )}
              {form.role_type !== 'company_director' && !isCompanyDirector && (
                <small style={{ color: '#475569', marginTop: 4, display: 'inline-block' }}>
                  💡 The Company Director (co-founder) role is only visible/assignable when signed in as an existing Company Director.
                </small>
              )}
            </div>
            <div className={styles.formRow}>
              <label>Account profile</label>
              <select
                value={form.account_profile}
                onChange={(e) => setForm({ ...form, account_profile: e.target.value })}
              >
                {ACCOUNT_PROFILE_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <small>{ACCOUNT_PROFILE_OPTIONS.find((p) => p.value === form.account_profile)?.desc} — a label only, used as the default for History &amp; Document Rights settings. Never grants rights by itself.</small>
            </div>
            <div className={styles.formRow}>
              <label>Account Status</label>
              <select
                value={form.is_active ? 'active' : 'inactive'}
                onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive (cannot login)</option>
              </select>
            </div>

            <div className={styles.permSection}>
              <div className={styles.permSectionTitle}>Individual Access Permissions</div>
              <div className={styles.permGrid}>
                {PERMISSION_DEFS.map((p) => (
                  <label key={p.key} className={styles.permItem}>
                    <input
                      type="checkbox"
                      checked={!!form.permissions[p.key]}
                      onChange={() => togglePerm(p.key)}
                    />
                    <div className={styles.permLabel}>
                      <strong>{p.label}</strong>
                      <span>{p.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => { setEditModalOpen(false); setEditingId(null); setForm({ ...emptyForm }); }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.saveBtn}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
